import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from './api';

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function useApi<T>(path: string | null, intervalMs = 0): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      const r = await api.get<T>(path);
      setData(r);
      setError(null);
    } catch (e) {
      if (e instanceof ApiClientError) {
        // 401 means the user is signed out — let AuthProvider handle that.
        // Other errors: surface for the UI to render an empty/fallback state.
        if (e.status !== 401) setError(e.message);
        setData(null);
      } else {
        setError('Network error');
      }
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void refetch();
    if (!intervalMs) return;
    const id = setInterval(refetch, intervalMs);
    return () => clearInterval(id);
  }, [refetch, intervalMs]);

  return { data, loading, error, refetch };
}

// ---- Portfolio ----
export interface PortfolioSummary {
  totalValue: number;
  invested: number;
  marketValue: number;
  cash: number;
  /** Cash whose every movement we hold (a manual account). */
  cashLedger: number;
  /** What a connected broker leaves you to trade with (buying power). */
  cashMirrored: number;
  buyingPower: number;
  /** The broker's own balance — still counts unsettled buys, so context only. */
  settledCash: number;
  totalGain: number;
  totalGainPct: number;
  dayChange: number;
  dayChangePct: number;
  /** Which session dayChange covers. 'lastSession' before the bell and at weekends. */
  dayOf?: 'today' | 'lastSession';
  positions: number;
}

export interface PortfolioHolding {
  sym: string;
  name: string | null;
  sector: string | null;
  qty: number;
  avg: number;
  current: number;
  change: number;
  value: number;
  cost: number;
  pnl: number;
  pnlPct: number;
  dayPnl: number;
  openedAt: string;
  color: string | null;
  liveSource: string | null;
  /** Which account holds it — set on every row, so the aggregate can say. */
  broker: string;
  brokerName: string;
}

/** A place the user's stock and cash actually sits. 'all' is every one added up. */
export interface BrokerAccountView {
  id: string;
  slug: string;
  name: string;
  kind: 'manual' | 'stockintel';
  isDefault: boolean;
  syncedAt: string | null;
}

export const useBrokerAccounts = (refreshMs = 60_000) =>
  useApi<{ brokers: BrokerAccountView[] }>('/portfolio/brokers', refreshMs);

/** `broker` is a slug, or 'all' for the aggregate. */
const scoped = (path: string, broker: string) => (broker && broker !== 'all' ? `${path}?broker=${encodeURIComponent(broker)}` : path);

export const usePortfolioSummary = (refreshMs = 10_000, broker = 'all') =>
  useApi<PortfolioSummary>(scoped('/portfolio', broker), refreshMs);

export const usePortfolioHoldings = (refreshMs = 10_000, broker = 'all') =>
  useApi<{ count: number; holdings: PortfolioHolding[] }>(scoped('/portfolio/holdings', broker), refreshMs);

export interface EquityPoint {
  ts: string;
  marketValue: number;
  cash: number;
  totalValue: number;
  dayChange: number;
  kind: 'EOD' | 'TRADE';
}
export type PortfolioRange = '1D' | '1W' | '1M' | '3M' | '1Y' | 'All';

export const usePortfolioHistory = (range: PortfolioRange, refreshMs = 60_000) =>
  useApi<{ range: string; count: number; points: EquityPoint[] }>(
    `/portfolio/history?range=${range}`,
    refreshMs,
  );

export interface MoverItem {
  sym: string;
  last: number;
  reference: number;
  referenceKind: 'prevClose' | 'priorClose' | 'open';
  change: number;
  changePct: number;
  volume?: number;
  source: string;
}
/** live = whole market today · active = the worker's hot set (holdings/watchlists) · lastSession = last close vs the one before */
export type MoversMode = 'live' | 'active' | 'lastSession';
export interface MoversResponse {
  asOf: string;
  mode: MoversMode;
  universeSize?: number;
  gainers: MoverItem[];
  losers: MoverItem[];
}

export const useMovers = (limit = 4, refreshMs = 15_000, mode: MoversMode = 'live') =>
  useApi<MoversResponse>(`/stocks/movers?limit=${limit}&mode=${mode}`, refreshMs);

export interface IndexSnapshot {
  sym: string;
  value: number;
  prevClose: number | null;
  dayChange: number | null;
  dayChangePct: number | null;
  volume: number | null;
  asOf: string;
  source: 'dps' | 'ahl';
  /** Today's intraday points (PKT day), oldest first. Empty before the first push. */
  series?: Array<{ ts: number; value: number }>;
}
export const useIndices = (refreshMs = 60_000) =>
  useApi<{ asOf: string; count: number; indices: IndexSnapshot[] }>(
    '/indices',
    refreshMs,
  );

export const addWatchlistItem = (watchlistId: string, symbol: string) =>
  api.post(`/watchlists/${watchlistId}/items`, { symbol });

export const removeWatchlistItem = (watchlistId: string, symbol: string) =>
  api.del(`/watchlists/${watchlistId}/items/${encodeURIComponent(symbol)}`);

export const createWatchlist = (name: string) =>
  api.post<{ watchlist: { id: string; name: string; isDefault: boolean } }>('/watchlists', {
    name,
  });

export const deleteWatchlist = (id: string) => api.del(`/watchlists/${id}`);

export const renameWatchlist = (id: string, name: string) =>
  api.patch(`/watchlists/${id}`, { name });

export const setDefaultWatchlist = (id: string) =>
  api.patch(`/watchlists/${id}`, { isDefault: true });

export const reorderWatchlistItems = (id: string, symbols: string[]) =>
  api.put(`/watchlists/${id}/items/order`, { symbols });

/** One sale, matched by same-day netting and FIFO delivery cost (see /portfolio/realized). */
export interface RealizedTradeRow {
  id: string;
  symbol: string;
  name: string;
  qty: number;
  avgBuy: number;
  avgSell: number;
  totalCost: number;
  totalValue: number;
  fees: number;
  /** Gross profit before acquisition and disposal charges. */
  pnl: number;
  pnlNet: number;
  pnlPct: number;
  closedAt: string;
}

export interface DividendRow {
  id: string;
  symbol: string;
  perShare: string;
  qty: number;
  amount: string;
  tax: string;
  zakat: string;
  exDate: string | null;
  payDate: string;
}

export const useRealized = (refreshMs = 0, broker = 'all') =>
  useApi<{ count: number; realized: RealizedTradeRow[]; issues: string[]; totals: { pnlNetYtd: number } }>(`/portfolio/realized?broker=${encodeURIComponent(broker)}`, refreshMs);

export const useDividends = (refreshMs = 0) =>
  useApi<{ count: number; dividends: DividendRow[] }>('/portfolio/dividends', refreshMs);

// ---- Wallet ----
export interface WalletBalance {
  totalCash: number;
  available: number;
  inOpenOrders: number;
  unsettled: number;
  dividendsPocket: number;
}

export interface WalletTxn {
  id: string;
  type: 'DEPOSIT' | 'WITHDRAW' | 'BUY' | 'SELL' | 'DIVIDEND' | 'FEE' | 'ADJUSTMENT';
  amount: string;
  balanceAfter: string;
  method: string | null;
  counterparty: string | null;
  reference: string | null;
  occurredAt: string;
}

export const useWalletBalance = (refreshMs = 10_000, broker = 'all') =>
  useApi<WalletBalance>(scoped('/wallet/balance', broker), refreshMs);

export interface TradeRow {
  id: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  qty: number;
  price: string;
  fees: string;
  executedAt: string;
  source: string;
}
export const useTrades = (limit = 20, refreshMs = 0) =>
  useApi<{ count: number; trades: TradeRow[] }>(`/trades?limit=${limit}`, refreshMs);

export const useWalletTxns = (refreshMs = 0, broker = 'all') =>
  useApi<{ count: number; transactions: WalletTxn[] }>(scoped('/wallet/transactions', broker), refreshMs);

// ---- Watchlists ----
export interface WatchlistDto {
  id: string;
  name: string;
  isDefault: boolean;
  items: string[];
}

export const useWatchlists = (refreshMs = 0) =>
  useApi<{ watchlists: WatchlistDto[] }>('/watchlists', refreshMs);

// ---- Mutations ----
export const placeTrade = (body: {
  symbol: string;
  side: 'BUY' | 'SELL';
  qty: number;
  price: number;
  fees?: number;
  executedAt?: string;
  notes?: string;
}) => api.post<{ trade: { id: string } }>('/trades', body);

export const quickAddHolding = (body: {
  symbol: string;
  qty: number;
  avgPrice: number;
  buyDate: string;
  notes?: string;
}) => api.post<{ trade: { id: string } }>('/trades/quick-add', body);

export interface SymbolHit {
  sym: string;
  name: string;
  market: string;
  sector: string | null;
  lastPrice: string | null;
  dayChangePct: string | null;
  /** false when the worker has flagged the symbol as not being served upstream. */
  isActive?: boolean;
  inactiveReason?: string | null;
}

export const searchSymbols = (q: string, limit = 10) =>
  api.get<{ count: number; results: SymbolHit[] }>(
    `/symbols/search?q=${encodeURIComponent(q)}&limit=${limit}`,
  );

export const deposit = (body: {
  amount: number;
  method?: 'Raast' | 'IBAN' | 'IBFT';
  counterparty?: string;
}) => api.post('/wallet/deposit', body);

export const withdraw = (body: {
  amount: number;
  method?: 'Raast' | 'IBAN' | 'IBFT';
  counterparty?: string;
}) => api.post('/wallet/withdraw', body);
